import Moment from 'moment';

import AMENITIES from '../../scripts/constants/amenities';
import { PREMIER } from '../../scripts/constants/premier-types';

class SegmentUiModel {
  constructor({
    checkinIneligibleReasons = undefined,
    segment = undefined,
  } = {}) {
    this.checkinIneligibleReasons = checkinIneligibleReasons;
    if (segment !== undefined) {
      this.aircraftType = segment.aircraftType;
      this.arrivalAirport = segment.arrivalAirport;
      if (segment.arrivalDateTime) {
        Moment.locale('es-us');
        this.arrivalDateTime = Moment.utc(segment.arrivalDateTime);
      }
      this.amenitiesPremiumCabin = segment.amenitiesPremiumCabin;
      this.amenitiesMainCabin = segment.amenitiesMainCabin;
      this.arrivalTerminal = segment.arrivalTerminal;
      this.arrivalTimeZoneId = segment.arrivalTimeZoneId;
      this.arrivalTimeZoneName = segment.arrivalTimeZoneName;
      this.bookingClass = segment.bookingClass;
      this.cabin = segment.cabin;
      this.codeShare = segment.codeShare;
      this.departureAirport = segment.departureAirport;
      if (segment.departureDateTime) {
        Moment.locale('es-us');
        this.departureDateTime = Moment.utc(segment.departureDateTime);
      }
      this.departureTerminal = segment.departureTerminal;
      this.domestic = segment.domestic;
      this.fareAndCabinName = segment.fareAndCabinName;
      this.fareBasisCode = segment.fareBasisCode;
      this.fareFamily = segment.fareFamily;
      this.flightDurationInMinutes = segment.flightDurationInMinutes;
      this.flightStatus = segment.flightStatus;
      this.isCancelOag = segment.isCancelOag;
      this.isCancelOds = segment.isCancelOds;
      this.isChangeOag = segment.isChangeOag;
      this.isChangeOds = segment.isChangeOds;
      this.isPremierAvailable = segment.isPremierAvailable;
      this.isPremierLight = segment.isPremierLight;
      this.isPremierOne = segment.isPremierOne;
      this.layoverToNextSegmentsInMinutes = segment.layoverToNextSegmentsInMinutes;
      this.marketFlightFromLeg = segment.marketFlightFromLeg;
      this.marketingCarrier = segment.marketingCarrier;
      this.marketingFlightCode = segment.marketingFlightCode;
      this.operatingCarrier = segment.operatingCarrier;
      this.operatingFlightCode = segment.operatingFlightCode;
      this.passFlight = segment.passFlight;
      this.regionKeyFromLeg = segment.regionKeyFromLeg;
      this.segmentCode = segment.segmentCode;
      this.segmentStatus = segment.segmentStatus;
      this.segmentRegion = segment.segmentRegion;
      this.stops = segment.stops;
      this.timeZoneId = segment.timeZoneId;
      this.timeZoneName = segment.timeZoneName;
    }
  }

  checkIfMainCabinHasAmenity() {
    return this.amenitiesMainCabin && this.amenitiesMainCabin.complimentaryFeatures && this.amenitiesMainCabin.complimentaryFeatures.length;
  }

  checkIfMainCabinHasPaidAmenity() {
    return this.amenitiesMainCabin && this.amenitiesMainCabin.paidFeatures && this.amenitiesMainCabin.paidFeatures.length;
  }

  checkIfPremiumCabinHasPaidAmenity() {
    return this.amenitiesPremiumCabin && this.amenitiesPremiumCabin.paidFeatures.length;
  }
  /**
  * @Return {String} ArrivalDateTime with format
  */
  getArrivalDateTime(format) {
    return this.arrivalDateTime.format(format);
  }

  /**
  * @Return {String} DepartureDateTime with format
  */
  getDepartureDateTime(format) {
    return this.departureDateTime.format(format);
  }

  getRoutes() {
    return {
      'departureAirport' : this.departureAirport,
      'arrivalAirport' : this.arrivalAirport,
      'segmentCode': this.segmentCode,
      'flightNumber': this.marketingFlightCode,
      'segmentStatus': this.segmentStatus,
    };
  }

  getArrayOfMainCabinAmenities(limit) {
    return combineFeatures(this.amenitiesMainCabin, limit);
  }

  getArrayOfPremiumCabinAmenities() {
    const premiumAmenities = combineFeatures(this.amenitiesPremiumCabin, null);
    return premiumAmenities;
  }

  getOperatingCarrier() {
    return this.operatingCarrier;
  }

  getAircraftType() {
    return this.aircraftType;
  }

  getOperatingFlightCode() {
    return this.operatingFlightCode;
  }

  getFareFamily() {
    if (this.isBasicOrClassicFareFamily()) {
      return this.fareFamily;
    }
    if (this.isPremierLight) {
      return 'PREMIER_LIGHT';
    } else if (this.isPremierOne) {
      return 'PREMIER_ONE';
    }
    return this.fareFamily;
  }

  isBasicFareFamily() {
    return this.fareFamily === 'BASIC' || 
           this.fareFamily === 'BASICLIGHT' || 
           this.bookingClass === 'V';
  }

  isBasicOrClassicFareFamily() {
    const touristBookingClasses = ['V', 'R', 'N', 'E', 'T', 'Q', 'L', 'H', 'K', 'U', 'M', 'B', 'Y', 'W'];
    return this.fareFamily === 'BASIC' || 
           this.fareFamily === 'BASICLIGHT' || 
           this.fareFamily === 'CLASSIC' || 
           touristBookingClasses.includes(this.bookingClass);
  }
  getPremierFareFamilyBySegment() {
    let fareFamily = '';
    if (this.isPremierAvailable) {
      fareFamily = PREMIER.PREMIER;
      if (this.isPremierOne) {
        fareFamily = PREMIER.ONE;
      } else if (this.isPremierLight) {
        fareFamily = PREMIER.LIGHT;
      }
    }
    return fareFamily;
  }
}

function combineFeatures({ complimentaryFeatures, paidFeatures }, limit) {
  /**
   * This is a hotfix: it could be removed once API complies with a not null, mandatory rule for these fields.
   */
  complimentaryFeatures = complimentaryFeatures || [];
  paidFeatures = paidFeatures || [];

  let allFeatures = paidFeatures.concat(complimentaryFeatures);

  allFeatures = allFeatures.filter(feature => {
    return AMENITIES[feature];
  }).map((feature) => {
    return {
      id: feature,
      isPaid: paidFeatures.includes(feature),
    };
  });

  if (limit) {
    return allFeatures.slice(0, limit);  // Only display first 5 amenities in 'Details'
  } else {
    return allFeatures;
  }
}

export default SegmentUiModel;
